import { read, write } from './storage';

/** The player's theme: dark by default; "system" follows the device (or Telegram inside Telegram). */
export type ThemePref = 'dark' | 'light' | 'system';
export const THEME_PREFS: ThemePref[] = ['dark', 'light', 'system'];

const KEY = 'edil.theme';
/** --color-bg of each theme (packages/ui tokens), for the browser and Telegram chrome. */
const BG = { dark: '#0c111d', light: '#f9fafb' } as const;

/** Telegram's own scheme, set at launch inside Telegram; used for "system". */
let telegramScheme: 'light' | 'dark' | null = null;
let onChrome: ((bg: string) => void) | null = null;

export function themePref(): ThemePref {
  const v = read(KEY);
  return v === 'light' || v === 'system' ? v : 'dark';
}

function resolved(pref: ThemePref): 'dark' | 'light' {
  if (pref !== 'system') return pref;
  if (telegramScheme) return telegramScheme;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Pins data-theme (the tokens read it) and colours the browser / Telegram bars to match. */
export function applyThemePref(pref: ThemePref = themePref()): void {
  const theme = resolved(pref);
  // On the web "system" leaves data-theme unset, so the tokens follow the device live.
  if (pref === 'system' && !telegramScheme) delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = theme;
  document.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.setAttribute('content', BG[theme]));
  onChrome?.(BG[theme]);
}

export function setThemePref(pref: ThemePref): void {
  write(KEY, pref);
  applyThemePref(pref);
}

/** Inside Telegram: its scheme (for "system") and a way to colour its header and background. */
export function attachTelegramTheme(scheme: 'light' | 'dark', chrome: (bg: string) => void): void {
  telegramScheme = scheme;
  onChrome = chrome;
  applyThemePref();
}
